import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';
import { toast } from 'sonner';

export const useSimpleUpdateNode = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, updates }: { id: string; updates: any }) => {
      console.log('Simple update node:', { id, updates });
      
      // Use the most basic update possible to avoid constraints
      const { data, error } = await supabase
        .from('nodes')
        .update(updates)
        .eq('id', id)
        .select('*')
        .single();

      if (error) {
        console.error('Simple update error:', error);
        throw new Error(`Update failed: ${error.message}`);
      }
      
      console.log('Simple update successful:', data);
      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['nodes'] });
      queryClient.invalidateQueries({ queryKey: ['node', data.id] });
      toast.success('Content updated successfully');
    },
    onError: (error: any) => {
      console.error('Simple update mutation failed:', error);
      toast.error(error.message || 'Failed to update content');
    },
  });
};

export const useSimpleSubmitForReview = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ nodeId }: { nodeId: string }) => {
      console.log('Simple submit for review:', nodeId);
      
      // First verify the node exists and is a draft
      const { data: nodeCheck, error: checkError } = await supabase
        .from('nodes')
        .select('id, status, title')
        .eq('id', nodeId)
        .single();

      if (checkError) {
        console.error('Node check failed:', checkError);
        throw new Error(`Node not found: ${checkError.message}`);
      }

      if (nodeCheck.status !== 'draft') {
        throw new Error(`Cannot submit for review: content is currently ${nodeCheck.status}. Only draft content can be submitted.`);
      }

      // Simple status update
      const { data, error } = await supabase
        .from('nodes')
        .update({ status: 'in_review' })
        .eq('id', nodeId)
        .eq('status', 'draft')
        .select('*')
        .single();

      if (error) {
        console.error('Status update failed:', error);
        throw new Error(`Failed to submit for review: ${error.message}`);
      }

      if (!data) {
        throw new Error('No data returned from update - node may have been modified by another user');
      }

      console.log('Simple submit successful:', data);
      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['nodes'] });
      queryClient.invalidateQueries({ queryKey: ['node', data.id] });
      toast.success('Content submitted for review successfully');
    },
    onError: (error: any) => {
      console.error('Simple submit mutation failed:', error);
      toast.error(error.message || 'Failed to submit content for review');
    },
  });
};

export const useSimpleCreateNode = () => {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async (nodeData: any) => {
      if (!user) throw new Error('User must be authenticated');

      console.log('Simple create node:', nodeData);

      const { data, error } = await supabase
        .from('nodes')
        .insert({
          ...nodeData,
          author_id: user.id,
        })
        .select('*')
        .single();

      if (error) {
        console.error('Simple create error:', error);
        throw new Error(`Create failed: ${error.message}`);
      }
      
      console.log('Simple create successful:', data);
      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['nodes'] });
      toast.success('Content created successfully');
    },
    onError: (error: any) => {
      console.error('Simple create mutation failed:', error);
      toast.error(error.message || 'Failed to create content');
    },
  });
};

export const useSimpleDeleteNode = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (nodeId: string) => {
      console.log('Simple delete node:', nodeId);

      const { error } = await supabase
        .from('nodes')
        .delete()
        .eq('id', nodeId);

      if (error) {
        console.error('Simple delete error:', error);
        throw new Error(`Delete failed: ${error.message}`);
      }
      
      console.log('Simple delete successful');
      return nodeId;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['nodes'] });
      toast.success('Content deleted successfully');
    },
    onError: (error: any) => {
      console.error('Simple delete mutation failed:', error);
      toast.error(error.message || 'Failed to delete content');
    },
  });
}; 